import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import heroBikeAction from "@/assets/hero-bike-action.jpg";
import heroBikeStatic from "@/assets/hero-bike-static.jpg";
import CartButton from "@/components/CartButton";
import { WelcomePopup } from "@/components/WelcomePopup";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const Index = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Featured SurRon Electric Bikes",
    "description": "High-performance electric dirt bikes from SurRon - Ultra Bee, Light Bee X, Hyper Bee",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Product",
          "name": "SurRon Light Bee X Thunder Pro",
          "description": "8000W peak power electric dirt bike with 47 mile range",
          "brand": { "@type": "Brand", "name": "SurRon" }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Product",
          "name": "SurRon Ultra Bee Storm Elite",
          "description": "12.5kW peak power electric motorcycle with 75 mile range",
          "brand": { "@type": "Brand", "name": "SurRon" }
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title="BeeX Performance | SurRon Electric Bikes Egypt - Ultra Bee, Light Bee X, L1E"
        description="Egypt's #1 SurRon dealer. Shop Ultra Bee 21KW, Light Bee X (LBX), Hyper Bee electric dirt bikes. EBMX controllers, motors & DHS batteries. CRF-style performance."
        keywords="SurRon Egypt, Ultra Bee, Light Bee X, LBX, L1E, Hyper Bee, electric dirt bike, EBMX, DHS battery, CRF electric bike, BeeX Performance"
        canonicalUrl="https://beexperformance.com/"
        structuredData={structuredData}
      />
      
      <WelcomePopup />
      
      {/* Navigation */}
      <header>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10" aria-label="Main navigation">
          <div className="container mx-auto px-6 py-5 flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold tracking-tight" aria-label="BeeX Performance Home">
              <span className="text-accent">BeeX</span>
            </Link>
            <div className="flex gap-6 items-center text-sm font-medium">
              <Link to="/store" className="text-white hover:text-accent transition-colors">
                Store
              </Link>
              <Link to="/auth" className="text-white hover:text-accent transition-colors">
                Login
              </Link>
              <CartButton />
            </div>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section 1 - Light Bee X */}
        <section className="relative min-h-screen w-full overflow-hidden" aria-labelledby="hero-lbx-title">
          <img 
            src={heroBikeAction}
            alt="SurRon Light Bee X electric dirt bike in action - 8000W performance electric motorcycle"
            className="absolute inset-0 w-full h-full object-cover object-center"
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 hero-overlay" aria-hidden="true" />
          
          <div className="relative z-10 min-h-screen flex flex-col justify-between container mx-auto px-4 sm:px-6">
            {/* Product Label */}
            <div className="pt-24 sm:pt-32 flex justify-center sm:justify-end">
              <span className="text-accent text-base sm:text-lg font-bold tracking-wide">SurRon Light Bee X</span>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center sm:justify-end py-8 sm:py-0">
              <div className="text-center sm:text-right max-w-2xl px-2">
                <h1 id="hero-lbx-title" className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight text-shadow-strong">
                  Featherlight Arrival,<br />
                  <span className="text-white">Rules Redefined!</span>
                </h1>
                <p className="sr-only">
                  The SurRon Light Bee X (LBX) electric dirt bike delivers 8000W peak power in a lightweight 125lb package. 
                  Perfect for trail riding and off-road adventures with CRF-style performance.
                </p>
                <Link to="/store" aria-label="View SurRon Light Bee X in store">
                  <Button className="bg-accent text-black hover:bg-accent/90 px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base font-bold uppercase tracking-wide">
                    Shop Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Specs Grid */}
            <dl className="pb-8 sm:pb-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6 text-white" aria-label="Light Bee X Specifications">
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Peak Power</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">8000W</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Curb Weight</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">125lbs</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Maximum Torque</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">196ft-lbs</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">WMTC Range</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">47miles</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Battery Type</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">60V/40Ah</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Top Speed</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">47mph</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Hero Section 2 - Ultra Bee */}
        <section className="relative min-h-screen w-full overflow-hidden" aria-labelledby="hero-ultra-title">
          <img 
            src={heroBikeStatic}
            alt="SurRon Ultra Bee 21KW electric motorcycle - high performance electric bike for roads and trails"
            className="absolute inset-0 w-full h-full object-cover object-center"
            width={1920}
            height={1080}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 hero-overlay" aria-hidden="true" />
          
          <div className="relative z-10 min-h-screen flex flex-col justify-between container mx-auto px-4 sm:px-6">
            {/* Product Label */}
            <div className="pt-24 sm:pt-32 flex justify-center sm:justify-end">
              <span className="text-accent text-base sm:text-lg font-bold tracking-wide">SurRon Ultra Bee</span>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center sm:justify-end py-8 sm:py-0">
              <div className="text-center sm:text-right max-w-2xl px-2">
                <h2 id="hero-ultra-title" className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight text-shadow-strong">
                  Ultra Speed Ahead<br />
                  <span className="text-white">Leave the Rest Behind</span>
                </h2>
                <p className="sr-only">
                  The SurRon Ultra Bee delivers 21KW of power with a 75-mile range. 
                  The ultimate electric motorcycle for performance enthusiasts seeking L1E certification.
                </p>
                <Link to="/store" aria-label="View SurRon Ultra Bee in store">
                  <Button className="bg-accent text-black hover:bg-accent/90 px-6 sm:px-8 py-4 sm:py-6 text-sm sm:text-base font-bold uppercase tracking-wide">
                    Shop Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Specs Grid */}
            <dl className="pb-8 sm:pb-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6 text-white" aria-label="Ultra Bee Specifications">
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Peak Power</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">12.5kW</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Lightweight</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">187lbs</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Wheel Torque</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">325ft-lbs</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Max Range</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">75miles</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Li-ion Battery</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">74V/55Ah</dd>
              </div>
              <div className="text-center">
                <dt className="text-xs sm:text-sm text-muted-foreground order-2">Top Speed</dt>
                <dd className="text-xl sm:text-3xl font-bold mb-1 order-1">59mph</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Browse All CTA */}
        <section className="relative bg-black py-20 border-t border-white/10" aria-labelledby="cta-title">
          <div className="container mx-auto px-6 text-center">
            <h2 id="cta-title" className="text-4xl font-bold text-white mb-4">
              Explore All SurRon Models & Parts
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Discover our complete range of high-performance electric bikes, EBMX upgrades, DHS batteries, and accessories
            </p>
            <Link to="/store" aria-label="Browse all electric bikes and parts">
              <Button className="bg-accent text-black hover:bg-accent/90 px-10 py-6 text-base font-bold uppercase tracking-wide">
                View Store
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
